import { lastDayOf, type Format, type TimeGrain } from "@cd/core";

const cache = new Map<string, Intl.NumberFormat>();
const nf = (key: string, opts: Intl.NumberFormatOptions) => {
  let f = cache.get(key);
  if (!f) cache.set(key, (f = new Intl.NumberFormat("en-US", opts)));
  return f;
};

export function formatValue(v: unknown, format?: Format, opts: { compact?: boolean; axis?: boolean } = {}): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v !== "number") return String(v);
  // Axis ticks: always compact, whole steps (0 / $3K / $6K), so every tick reads the same way.
  if (opts.axis) {
    // Up to two decimals so a small rate's ticks (0.02% / 0.04%) don't all read "0%".
    if (format === "percent") return `${nf("pct-a", { maximumFractionDigits: 2 }).format(v)}%`;
    const f = format === "currency"
      ? nf("cur-a", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 })
      : nf("num-a", { notation: "compact", maximumFractionDigits: 1 });
    return f.format(v);
  }
  const compact = opts.compact && Math.abs(v) >= 10_000;
  switch (format) {
    case "currency":
      return compact
        ? nf("cur-c", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(v)
        : nf("cur", { style: "currency", currency: "USD" }).format(v);
    case "percent":
      // Always two decimals: small rates (a 0.05% unsubscribe rate) would otherwise round to "0%".
      return `${nf("pct", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)}%`;
    case "integer":
      return compact ? nf("int-c", { notation: "compact", maximumFractionDigits: 1 }).format(v) : nf("int", { maximumFractionDigits: 0 }).format(v);
    default:
      return compact
        ? nf("num-c", { notation: "compact", maximumFractionDigits: 1 }).format(v)
        : nf("num", { maximumFractionDigits: 2 }).format(v);
  }
}

/** Display label for a raw category value: `dine_in` → `Dine in`. The underlying value is unchanged. */
export function humanize(v: unknown): string {
  if (v === null || v === undefined) return "(none)";
  const s = String(v);
  return /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/.test(s) ? s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : s;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function formatTimeBucket(v: unknown, grain?: TimeGrain): string {
  // ISO weekday, 1 = Monday. Some drivers return integers as strings.
  if (grain === "day_of_week") return v === null || v === undefined ? "(none)" : WEEKDAYS[Number(v) - 1] ?? String(v);
  if (typeof v !== "string") return formatValue(v);
  const [y, m, d] = [v.slice(0, 4), Number(v.slice(5, 7)), Number(v.slice(8, 10))];
  const mon = MONTHS[m - 1] ?? "";
  switch (grain) {
    case "hour": return `${mon} ${d} ${v.slice(11, 16)}`;
    case "week": return `Wk of ${mon} ${d}`;
    case "month": return `${mon} ${y}`;
    case "quarter": return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
    case "year": return y;
    default: return `${mon} ${d}`;
  }
}

/** An inclusive date range for labels: `Sep 8 – Sep 14, 2025`. */
export function formatRange(r: { start: string; end: string }): string {
  const day = (s: string) => new Date(`${s.slice(0, 10)}T00:00:00Z`);
  const a = day(r.start);
  const b = day(lastDayOf(r));
  const md = (d: Date) => `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  if (b <= a) return `${md(a)}, ${a.getUTCFullYear()}`;
  return a.getUTCFullYear() === b.getUTCFullYear()
    ? `${md(a)} – ${md(b)}, ${b.getUTCFullYear()}`
    : `${md(a)}, ${a.getUTCFullYear()} – ${md(b)}, ${b.getUTCFullYear()}`;
}

/** Percent change, or null when the base is zero/missing. */
export function delta(current: unknown, previous: unknown): number | null {
  if (typeof current !== "number" || typeof previous !== "number" || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/** Reference categorical palette (validated for CVD in both modes). Order is the safety mechanism. */
export const PALETTE = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
  dark: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
} as const;
